import { createSelector } from 'reselect';
import { denormalize } from 'normalizr';

import * as schemas from 'store/common/schemas';
import * as entitySelectors from 'store/entities/selectors';
import * as teamMembersHelpers from 'store/features/company-team-member-connections/helpers';
import * as advisorHelpers from 'store/features/company-advisor-connections/helpers';
import { selectCurrentUser } from 'store/features/auth/selectors';

const selectCompaniesFeatureState = state => state.features.companies;

export const selectSmartContractDetails = createSelector();

export const selectCompanyByPrettyId = createSelector(
  entitySelectors.selectAllEntities,
  (state, props) => props.prettyId,
  (entities, prettyId) => denormalize(prettyId, schemas.company, entities)
);

export const selectIsFetchingCompanyByPrettyId = createSelector(
  selectCompaniesFeatureState,
  (state, props) => props.prettyId,
  (companiesFeatureState, prettyId) =>
    companiesFeatureState[prettyId] && companiesFeatureState[prettyId].fetching
);

export const selectIsCompanyFetchedAtleastOnceByPrettyId = createSelector(
  selectCompaniesFeatureState,
  (state, props) => props.prettyId,
  (companiesFeatureState, prettyId) =>
    companiesFeatureState[prettyId] && !!companiesFeatureState[prettyId].lastFetchedAt
);

export const selectCompanyTeamMembersByPrettyId = createSelector(
  entitySelectors.selectCompanyEntities,
  (state, props) => props.prettyId,
  entitySelectors.selectAllEntities,
  (companyEntities, prettyId, allEntities) => {
    const company = companyEntities[prettyId];

    let teamMembers = [];

    if (company.teamMembers) {
      Array.prototype.push.apply(
        teamMembers,
        denormalize(
          company.teamMembers,
          [schemas.companyTeamMemberConnection],
          allEntities
        ).map(ac => ({
          ...ac,
          status: teamMembersHelpers.calculateTeamMemberConnectionStatus(ac),
        }))
      );
    }

    if (company.additionalData) {
      const teamMembersFromAdditionalData = company.additionalData.team || [];
      Array.prototype.push.apply(
        teamMembers,
        teamMembersFromAdditionalData
          .map(
            a =>
              a.user
                ? {
                    ...a,
                    status: advisorHelpers.ADVISOR_CONNECTION_STATUSES.pendingSignUpByUser,
                  }
                : null
          )
          .filter(Boolean)
      );
    }

    return teamMembers;
  }
);

export const selectIsFetchingCompanyTeamMembersByPrettyId = createSelector(
  selectCompaniesFeatureState,
  (state, props) => props.prettyId,
  (companiesFeatureState, prettyId) =>
    companiesFeatureState[prettyId] && companiesFeatureState[prettyId].fetchingTeamMembers
);

export const selectIsCompanyTeamMembersFetchedAtleastOnceByPrettyId = createSelector(
  selectCompaniesFeatureState,
  (state, props) => props.prettyId,
  (companiesFeatureState, prettyId) =>
    companiesFeatureState[prettyId] && !!companiesFeatureState[prettyId].lastFetchedTeamMembersAt
);

export const selectCompanyAdvisorsByPrettyId = createSelector(
  entitySelectors.selectCompanyEntities,
  (state, props) => props.prettyId,
  entitySelectors.selectAllEntities,
  (companyEntities, prettyId, allEntities) => {
    const company = companyEntities[prettyId];

    if (company) {
      let advisors = [];
      if (company.advisors) {
        Array.prototype.push.apply(
          advisors,
          denormalize(
            company.advisors,
            [schemas.companyAdvisorConnection],
            allEntities
          ).map(ac => ({
            ...ac,
            type: advisorHelpers.calculateAdvisorConnectionType(ac),
            status: advisorHelpers.calculateAdvisorConnectionStatus(ac),
          }))
        );
      }
      if (company.additionalData) {
        const advisorsFromAdditionalData = company.additionalData.advisors || [];
        Array.prototype.push.apply(
          advisors,
          advisorsFromAdditionalData
            .map(
              a =>
                a.user
                  ? {
                      ...a,
                      status: advisorHelpers.ADVISOR_CONNECTION_STATUSES.pendingSignUpByUser,
                    }
                  : null
            )
            .filter(Boolean)
        );
      }
      return advisors;
    } else {
      return [];
    }
  }
);

export const selectIsFetchingCompanyAdvisorsByPrettyId = createSelector(
  selectCompaniesFeatureState,
  (state, props) => props.prettyId,
  (companiesFeatureState, prettyId) =>
    companiesFeatureState[prettyId] && companiesFeatureState[prettyId].fetchingAdvisors
);

export const selectIsCompanyAdvisorsFetchedAtleastOnceByPrettyId = createSelector(
  selectCompaniesFeatureState,
  (state, props) => props.prettyId,
  (companiesFeatureState, prettyId) =>
    companiesFeatureState[prettyId] && !!companiesFeatureState[prettyId].lastFetchedAdvisorsAt
);

export const selectIsCurrentUserAclMemberOfCompanyByPrettyId = createSelector(
  selectCurrentUser,
  (state, props) => props.prettyId,
  (currentUser, prettyId) =>
    currentUser &&
    currentUser.myCompanies &&
    (currentUser.myCompanies || []).find(c => c && c.prettyId === prettyId)
);



// WEBPACK FOOTER //
// ./src/store/features/companies/selectors.js